import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Typography } from 'transfix-ui/core'
import { autoConfirmStop } from 'utils/actions/shipmentStop'
import { updateAppointmentReference } from 'apps/schedule/utils/actions/confirmReference'
import { TextInput } from 'components/atoms'
import { capitalize } from 'utils/capitalize'
import { confirmText, alreadyConfirmedText, appointmentDate } from '../../utils/deprecatedSchedule'

/**
 * @deprecated Please delete this file when mpNewSchedulerPage got turned on in prod
 */
export const MainStopCard = React.memo(
  ({ apptType, autoConfirmToken, confirmSuccess, errors, mainStop, stopType }) => {
    // Alright so this is confusing, but coupled enough that it's easier to just deal with it;
    // alreadyConfirmed (defined below, calculated by checking apopintmentStatus) is whether or not the appt was confirmed already;
    // confirmed is whether or not THIS APP SUCCESSFULLY CONFIRMED IT.
    const {
      appointmentReference,
      appointmentStatus,
      bol,
      city,
      confirmationReference,
      confirmed,
      endAtTime,
      isDeprecated,
      po,
      shipperName,
      startAtTime,
      stateCode,
      stopId,
    } = mainStop || {}

    const dispatch = useDispatch()

    const updateReference = (id, confirmationReference_) =>
      dispatch(updateAppointmentReference(id, confirmationReference_, autoConfirmToken))

    const alreadyConfirmed = appointmentStatus === 'confirmed'
    const alreadyConfirmedMemoized = useMemo(
      () => (alreadyConfirmed ? alreadyConfirmedText(mainStop) : null),
      [mainStop, alreadyConfirmed]
    )

    const confirmedMemoized = useMemo(() => (confirmed ? confirmText(mainStop, stopType) : null), [
      mainStop,
      stopType,
      confirmed,
    ])
    const appointmentDateMemoized = useMemo(() => appointmentDate(mainStop, apptType), [
      mainStop,
      apptType,
    ])

    const [apptRef, setApptRef] = useState('')
    const [validationMsg, setValidationMsg] = useState('')

    // The "title" message to be displayed if this request was not expired, and therefore can be auto-confirmed
    const confirmMessage = () => {
      if (alreadyConfirmed) {
        return alreadyConfirmedMemoized
      }
      if (confirmed) {
        return confirmedMemoized
      }
      return null
    }

    const expiredSubheader = 'Please review and confirm the new appointment request'

    const changeApptRefHandler = e => {
      setApptRef(e.target.value)
    }

    const submitApptRefHandler = () => {
      if (apptRef) {
        updateReference(stopId, apptRef)
      } else {
        setValidationMsg('Please enter an appointment reference')
      }
    }

    const expiredPath = isDeprecated && !alreadyConfirmed && !confirmed
    const confirmNewDateTime = () =>
      dispatch(autoConfirmStop(stopId, autoConfirmToken, startAtTime, endAtTime))

    return (
      <div>
        <div className='row no-gutter'>
          {!expiredPath && (
            <>
              <Typography variant='h2' className='col-xs-12 tc'>
                {confirmMessage()}
              </Typography>
              {!appointmentReference && (
                <p className='col-xs-8 xs-shift-2 tc'>
                  Please submit your confirmation number below
                </p>
              )}
            </>
          )}
          {expiredPath && (
            <>
              <Typography variant='h2' className='col-xs-12 tc'>
                This request has been updated
              </Typography>
              <p className='col-xs-8 xs-shift-2 tc'>{expiredSubheader}</p>
            </>
          )}
        </div>
        <Card>
          <p className='b mb1'>
            {capitalize(stopType || mainStop?.stopType)} on {appointmentDateMemoized}
          </p>
          <p>Shipper: {shipperName}</p>
          {city && stateCode && (
            <p>
              Location: {city}, {stateCode}
            </p>
          )}
          {po && <p>PO: {po}</p>}
          {bol && <p>BOL: {bol}</p>}
          {appointmentReference && <p>APPT#: {appointmentReference}</p>}
          {confirmationReference && (
            <p data-test='appt-conf'>Appointment Confirmation: {confirmationReference}</p>
          )}
          {!expiredPath && (
            <>
              {!confirmSuccess && !confirmationReference && (
                <div className='pt4'>
                  <div className='row no-gutter'>
                    <>
                      <div className='col-xs-8'>
                        <TextInput
                          dataTest='appt-ref-input'
                          label='Confirmation # (Optional)'
                          large
                          clearable={false}
                          placeholder='Enter confirmation number'
                          onChange={changeApptRefHandler}
                          value={apptRef}
                          invalid={validationMsg}
                          validationMessage={validationMsg}
                        />
                      </div>
                      <div className='col-xs-4 pt4 mt1 pl2'>
                        <Button
                          data-test='appt-conf-btn'
                          color='primary'
                          onClick={submitApptRefHandler}
                          size='large'>
                          Submit
                        </Button>
                      </div>
                    </>
                  </div>
                </div>
              )}
              {confirmSuccess && (
                <div className='pt4'>
                  <div className='row no-gutter'>
                    <InlineMessage
                      type={INLINE_MESSAGE_VARIANTS.INFO}
                      message='Confirmation number saved!'
                      dataTest='appt-conf-success'
                    />
                  </div>
                </div>
              )}
            </>
          )}
          {expiredPath && (
            <div className='pt4'>
              <div className='row no-gutter'>
                <Button color='primary' onClick={confirmNewDateTime} size='large'>
                  Confirm Appointment on {appointmentDateMemoized}
                </Button>
              </div>
            </div>
          )}
        </Card>
        <div className='mt4 mb5 tc'>
          If you need to make changes, please reply to the email we sent you.
        </div>
      </div>
    )
  }
)

MainStopCard.propTypes = {
  mainStop: PropTypes.shape({}).isRequired,
  errors: PropTypes.string.isRequired,
  stopType: PropTypes.string.isRequired,
  apptType: PropTypes.string.isRequired,
}
